/**
 * BottomSheet — shared bottom-sheet primitive.
 *
 * Wraps @radix-ui/react-dialog to get:
 *   - Focus trap (Radix manages the focus cycle within the sheet)
 *   - Escape key close
 *   - aria-modal="true" and role="dialog"
 *   - Scroll lock on the body while open
 *
 * Consumers: FilterBottomSheet, AuthPromptSheet.
 * Internal layout (header, form content, CTAs) stays in each consumer —
 * BottomSheet only owns the outer shell: backdrop, sheet surface, drag handle.
 *
 * Design system tokens:
 *   - surfaceCard (bg-white), radiusLg (rounded-t-2xl),
 *     shadowSheet (shadow-xl), spaceInsetMd (px-4)
 *   - brandFocusRing on the optional close button
 *
 * Motion: backdrop and sheet use motion-safe: transitions.
 */

import React from 'react';
import * as Dialog from '@radix-ui/react-dialog';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** Accessible label for the dialog (aria-label on the sheet panel). */
  title: string;
  /** Optional visual header to render inside the sheet above children. */
  header?: React.ReactNode;
  children: React.ReactNode;
  /** Max height of the sheet as a Tailwind class. Default: max-h-[90vh] */
  maxHeight?: string;
}

export function BottomSheet({
  isOpen,
  onClose,
  title,
  header,
  children,
  maxHeight = 'max-h-[90vh]',
}: Props) {
  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <Dialog.Portal>
        {/* Backdrop */}
        <Dialog.Overlay
          className="fixed inset-0 bg-black/40 z-40 motion-safe:animate-none"
          aria-hidden="true"
        />

        {/* Sheet */}
        <Dialog.Content
          aria-label={title}
          className={`fixed inset-x-0 bottom-0 z-50 bg-white rounded-t-2xl shadow-xl ${maxHeight} overflow-y-auto focus:outline-none`}
          onPointerDownOutside={() => onClose()}
          onEscapeKeyDown={() => onClose()}
        >
          {/* Drag handle — decorative */}
          <div className="flex justify-center pt-3 pb-1" aria-hidden="true">
            <div className="w-10 h-1 rounded-full bg-gray-300" />
          </div>

          {header}
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
